import { Link } from '@tanstack/react-router'
import { Mail, MessageSquareText, Phone } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { formatEventTime } from '@/lib/utils'
import { formatPhone } from '@/lib/phone'
import { useCustomerThreads, type CustomerThreadSummary } from '@/features/customer-comms/api'

const CHANNEL_ICON = { sms: MessageSquareText, phone: Phone } as const

function channelIcon(channel: string) {
  return CHANNEL_ICON[channel as keyof typeof CHANNEL_ICON] ?? Mail
}

/** Who the conversation is with: the person, else their address or number. */
function counterpart(thread: CustomerThreadSummary) {
  if (thread.recipient_phone && thread.channel !== 'email') return formatPhone(thread.recipient_phone)
  return thread.recipient_name?.trim() || thread.recipient_email || null
}

/** Every conversation with the customer, newest first; each opens under the customer (USA-1695). */
export function CustomerThreads({ customerGroupId, customerGroupUuid }: { customerGroupId: number; customerGroupUuid: string }) {
  const threads = useCustomerThreads(customerGroupId)
  if (threads.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }
  if (threads.isError) return <p className="text-sm text-destructive">Could not load conversations.</p>
  if (!threads.data?.length)
    return <p className="py-6 text-center text-sm text-muted-foreground">No emails, texts or calls yet.</p>
  return (
    <ul className="divide-y overflow-hidden rounded-xl border bg-card">
      {threads.data.map((thread) => {
        const Icon = channelIcon(thread.channel)
        const who = counterpart(thread)
        return (
          <li key={thread.id}>
            <Link
              to="/customers/$groupId/comms/$threadId"
              params={{ groupId: customerGroupUuid, threadId: String(thread.id) }}
              className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none sm:px-4"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300">
                <Icon className="size-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{thread.subject}</span>
                {who && <span className="block truncate text-xs text-muted-foreground">{who}</span>}
              </span>
              <time dateTime={thread.last_activity_at} className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatEventTime(thread.last_activity_at)}
              </time>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
